import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { api } from '../lib/api';

interface AccountSettingsPageProps {
  onNavigate: (page: string, data?: any) => void;
}

export default function AccountSettingsPage({ onNavigate }: AccountSettingsPageProps) {
  const { token, logout } = useAuth();
  const toast = useToast();

  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'profile' | 'password' | 'email' | 'username' | 'preferences' | 'danger'>('profile');

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // Email change
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // Username change
  const [newUsername, setNewUsername] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);

  // Mature toggle
  const [isMature, setIsMature] = useState(false);

  // Profile / Avatar
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [bio, setBio] = useState<string>('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Account deletion
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!token) {
      onNavigate('login');
      return;
    }
    fetch('/api/account/settings', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        setSettings(data);
        setIsMature(data.isMature || false);
        setAvatarUrl(data.avatarUrl || '');
        setBio(data.bio || '');
      })
      .catch(() => toast.error('Failed to load account settings.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    setSavingPassword(true);
    try {
      const res = await fetch('/api/account/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(data.error);
      }
    } catch {
      toast.error('Failed to update password.');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) {
      toast.error('Please enter a new email.');
      return;
    }
    setSavingEmail(true);
    try {
      const res = await fetch('/api/account/email', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ newEmail, password: emailPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setSettings((s: any) => ({ ...s, email: data.email, emailVerified: false }));
        setNewEmail('');
        setEmailPassword('');
      } else {
        toast.error(data.error);
      }
    } catch {
      toast.error('Failed to update email.');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleUsernameChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername.trim()) {
      toast.error('Please enter a new username.');
      return;
    }
    setSavingUsername(true);
    try {
      const res = await fetch('/api/account/username', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ newUsername }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setSettings((s: any) => ({ ...s, username: data.username }));
        setNewUsername('');
      } else {
        toast.error(data.error);
      }
    } catch {
      toast.error('Failed to update username.');
    } finally {
      setSavingUsername(false);
    }
  };

  const handleMatureToggle = async () => {
    const newValue = !isMature;
    try {
      const res = await fetch('/api/account/mature', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isMature: newValue }),
      });
      const data = await res.json();
      if (res.ok) {
        setIsMature(newValue);
        toast.success(data.message);
      } else {
        toast.error(data.error);
      }
    } catch {
      toast.error('Failed to update preference.');
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (deleteConfirmation !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" exactly to confirm.');
      return;
    }
    setDeleting(true);
    try {
      const res = await fetch('/api/account', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: deletePassword, confirmation: deleteConfirmation }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        logout();
        onNavigate('home');
      } else {
        toast.error(data.error);
      }
    } catch {
      toast.error('Failed to delete account.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const result = await api.upload.avatar(file);
      setAvatarUrl(result.url);
      toast.success('Avatar uploaded!');
    } catch {
      toast.error('Failed to upload avatar. Max 5MB, JPG/PNG/WebP only.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ bio, avatarUrl }),
      });
      const data = await res.json();
      if (res.ok) {
        setSettings((s: any) => ({ ...s, avatarUrl: data.avatarUrl, bio: data.bio }));
        toast.success('Profile updated!');
      } else {
        toast.error(data.error || 'Failed to update profile.');
      }
    } catch {
      toast.error('Failed to update profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const sections = [
    { id: 'profile' as const, label: 'Profile', icon: '🖼' },
    { id: 'password' as const, label: 'Password', icon: '🔒' },
    { id: 'email' as const, label: 'Email', icon: '📧' },
    { id: 'username' as const, label: 'Username', icon: '👤' },
    { id: 'preferences' as const, label: 'Preferences', icon: '⚙' },
    { id: 'danger' as const, label: 'Delete Account', icon: '⚠' },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Account Settings</h1>

      {/* Account info summary */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500 block">Username</span>
            <span className="font-medium">{settings?.username}</span>
          </div>
          <div>
            <span className="text-gray-500 block">Email</span>
            <span className="font-medium">{settings?.email}</span>
            {settings?.emailVerified ? (
              <span className="ml-2 text-xs text-green-400">Verified</span>
            ) : (
              <span className="ml-2 text-xs text-amber-400">Unverified</span>
            )}
          </div>
          <div>
            <span className="text-gray-500 block">Role</span>
            <span className="font-medium capitalize">{settings?.role?.toLowerCase()}</span>
          </div>
          <div>
            <span className="text-gray-500 block">Member since</span>
            <span className="font-medium">{settings?.createdAt ? new Date(settings.createdAt).toLocaleDateString() : '-'}</span>
          </div>
        </div>
        <div className="flex gap-2 mt-3 text-xs">
          {settings?.hasPassword && <span className="px-2 py-1 bg-gray-800 rounded-full text-gray-400">Password login</span>}
          {settings?.hasGoogle && <span className="px-2 py-1 bg-gray-800 rounded-full text-gray-400">Google connected</span>}
          {settings?.hasDiscord && <span className="px-2 py-1 bg-gray-800 rounded-full text-gray-400">Discord connected</span>}
        </div>
      </div>

      <div className="grid md:grid-cols-[200px_1fr] gap-6">
        {/* Sidebar nav */}
        <nav className="flex md:flex-col gap-1">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeSection === s.id
                  ? s.id === 'danger'
                    ? 'bg-red-900/20 text-red-400 border border-red-800'
                    : 'bg-ink-600/20 text-ink-400 border border-ink-600/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <span className="mr-2">{s.icon}</span>
              {s.label}
            </button>
          ))}
        </nav>

        {/* Content area */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <form onSubmit={handleProfileSave}>
              <h2 className="text-lg font-bold mb-4">Profile</h2>
              <div className="flex items-start gap-6 mb-6">
                {/* Avatar */}
                <div className="flex flex-col items-center gap-2">
                  <div className="w-24 h-24 rounded-full bg-gray-700 overflow-hidden border-2 border-gray-600">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl text-gray-500">
                        {settings?.username?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                  >
                    {uploadingAvatar ? 'Uploading...' : 'Change Photo'}
                  </button>
                  {avatarUrl && (
                    <button
                      type="button"
                      onClick={() => setAvatarUrl('')}
                      className="text-xs text-red-400 hover:text-red-300 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {/* Bio */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-300 mb-1">Bio</label>
                  <textarea
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    maxLength={500}
                    rows={5}
                    placeholder="Tell readers and other authors about yourself..."
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:border-ink-500 focus:outline-none resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1 text-right">{bio.length}/500</p>
                </div>
              </div>
              <button
                type="submit"
                disabled={savingProfile}
                className="px-5 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {savingProfile ? 'Saving...' : 'Save Profile'}
              </button>
            </form>
          )}

          {/* Password Section */}
          {activeSection === 'password' && (
            <form onSubmit={handlePasswordChange}>
              <h2 className="text-lg font-bold mb-4">Change Password</h2>
              {!settings?.hasPassword ? (
                <p className="text-gray-400 text-sm">
                  Your account uses social login and doesn't have a password set.
                  Use the <button onClick={() => onNavigate('forgot-password')} className="text-ink-400 hover:underline">forgot password</button> flow to set one.
                </p>
              ) : (
                <>
                  <div className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Current Password</label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={e => setCurrentPassword(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:border-ink-500 focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">New Password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:border-ink-500 focus:outline-none"
                        minLength={6}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Confirm New Password</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:border-ink-500 focus:outline-none"
                        minLength={6}
                        required
                      />
                      {confirmPassword && newPassword !== confirmPassword && (
                        <p className="text-red-400 text-xs mt-1">Passwords do not match.</p>
                      )}
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={savingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword}
                    className="mt-4 px-5 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
                  >
                    {savingPassword ? 'Updating...' : 'Update Password'}
                  </button>
                </>
              )}
            </form>
          )}

          {/* Email Section */}
          {activeSection === 'email' && (
            <form onSubmit={handleEmailChange}>
              <h2 className="text-lg font-bold mb-4">Change Email</h2>
              <p className="text-sm text-gray-400 mb-4">
                Current email: <span className="text-white font-medium">{settings?.email}</span>
                {settings?.emailVerified ? (
                  <span className="ml-2 text-xs text-green-400">(Verified)</span>
                ) : (
                  <span className="ml-2 text-xs text-amber-400">(Unverified)</span>
                )}
              </p>
              <div className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">New Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:border-ink-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={emailPassword}
                    onChange={e => setEmailPassword(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:border-ink-500 focus:outline-none"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={savingEmail || !newEmail || !emailPassword}
                className="mt-4 px-5 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {savingEmail ? 'Updating...' : 'Update Email'}
              </button>
            </form>
          )}

          {/* Username Section */}
          {activeSection === 'username' && (
            <form onSubmit={handleUsernameChange}>
              <h2 className="text-lg font-bold mb-4">Change Username</h2>
              <p className="text-sm text-gray-400 mb-4">
                Current username: <span className="text-white font-medium">{settings?.username}</span>
              </p>
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-300 mb-1">New Username</label>
                <input
                  type="text"
                  value={newUsername}
                  onChange={e => setNewUsername(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:border-ink-500 focus:outline-none"
                  minLength={3}
                  maxLength={30}
                  pattern="^[a-zA-Z0-9_-]+$"
                  title="Letters, numbers, underscores, and hyphens only"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">3-30 characters. Letters, numbers, underscores, and hyphens only.</p>
              </div>
              <button
                type="submit"
                disabled={savingUsername || !newUsername.trim()}
                className="mt-4 px-5 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {savingUsername ? 'Updating...' : 'Update Username'}
              </button>
            </form>
          )}

          {/* Preferences Section */}
          {activeSection === 'preferences' && (
            <div>
              <h2 className="text-lg font-bold mb-4">Preferences</h2>
              <div className="flex items-center justify-between max-w-md p-4 bg-gray-800 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Mature Content</p>
                  <p className="text-xs text-gray-400 mt-0.5">Show stories marked as 18+ in search and discovery</p>
                </div>
                <button
                  onClick={handleMatureToggle}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    isMature ? 'bg-ink-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                      isMature ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Danger Zone */}
          {activeSection === 'danger' && (
            <form onSubmit={handleDeleteAccount}>
              <h2 className="text-lg font-bold text-red-400 mb-2">Delete Account</h2>
              <div className="bg-red-900/10 border border-red-800/50 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-300">
                  This action is <strong>permanent and irreversible</strong>. All your stories, reviews, comments, messages,
                  reading progress, badges, and account data will be permanently deleted.
                </p>
              </div>
              <div className="space-y-4 max-w-md">
                {settings?.hasPassword && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Confirm Password</label>
                    <input
                      type="password"
                      value={deletePassword}
                      onChange={e => setDeletePassword(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-red-800/50 rounded-lg text-sm focus:border-red-500 focus:outline-none"
                      required
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Type <span className="text-red-400 font-mono">DELETE MY ACCOUNT</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmation}
                    onChange={e => setDeleteConfirmation(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-red-800/50 rounded-lg text-sm focus:border-red-500 focus:outline-none font-mono"
                    placeholder="DELETE MY ACCOUNT"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={deleting || deleteConfirmation !== 'DELETE MY ACCOUNT' || (settings?.hasPassword && !deletePassword)}
                className="mt-4 px-5 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Permanently Delete My Account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
